import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import {
  Currency,
  ReferralState,
} from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import {
  BodyL,
  BodyM,
  BodyS,
  HeadlineXL,
} from '@sorare/core/src/atoms/typography';
import { QRCode } from '@sorare/core/src/components/QRCode';
import { TermsDialog } from '@sorare/core/src/components/conversionCredit/TermsDialog';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { ReferralBar } from '@sorare/core/src/components/referral/ReferralBar';
import { InviteFriendsCta } from '@sorare/core/src/components/user/InviteFriendsCta';
import {
  CREATOR_PROGRAM,
  TERMS_AND_CONDITIONS,
} from '@sorare/core/src/constants/__generated__/routes';
import { Warning } from '@sorare/core/src/contexts/intl/Warning';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags.tsx';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { socialShareEventContext } from '@sorare/core/src/lib/events';
import { navLabels } from '@sorare/core/src/lib/glossary';
import { OPTIMISTIC_SLOTS_COUNT_PER_SPORT } from '@sorare/core/src/lib/so5';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { HowItWorks } from '#src/components/invite/HowItWorks/index.tsx';

import { CardsEarned } from './CardsEarned/index.tsx';
import { CreditsEarned } from './CreditsEarned/index.tsx';
import { Hub } from './Hub/index.tsx';
import { SetReferralOnlineLanding } from './SetReferralOnlineLanding/index.tsx';
import { VerifyPhoneBanner } from './VerifyPhoneBanner/index.tsx';
import type {
  OnlineLanding_config,
  OnlineLanding_currentUser,
} from './__generated__/index.graphql.ts';

const MAX_REFERRALS = 30; // This is also defined in the backend

const messages = defineMessages({
  pendingReview: {
    defaultMessage: 'Your account is under review.',
  },
  pendingReviewDescription: {
    defaultMessage:
      'We are reviewing this referral for authenticity and checking if it follows our <link>Terms and Conditions</link> before rewarding you. This process takes up to 1 week.',
  },
  goAffiliate: {
    defaultMessage: 'Join the Sorare Creator Program',
  },
  goAffiliateDescription: {
    defaultMessage:
      "You've reached the maximum number of completed referrals through our Referral Program. To continue benefiting from sharing Sorare, we invite you to join our <link>Creator Program</link> for further opportunities.",
  },
});

const Root = styled.section`
  display: grid;
  grid-template-areas: 'header' 'right' 'qr' 'howItWorks';
  gap: var(--double-unit);
  background: radial-gradient(
    circle at bottom right,
    rgba(54, 13, 119, 0.5) 0%,
    rgba(54, 13, 119, 0.5) 10%,
    rgb(0, 0, 0) 60%
  );
  background-attachment: fixed;
  padding: var(--double-unit) var(--double-unit) var(--quadruple-unit);
  @media ${desktopAndAbove} {
    display: flex;
    padding: 0;
  }
`;
const Left = styled.div`
  display: contents;
  @media ${desktopAndAbove} {
    display: flex;
    flex-direction: column;
    gap: var(--double-unit);
    padding: var(--double-unit) 0 var(--quadruple-unit) var(--double-unit);
  }
`;
const Header = styled.header`
  grid-area: header;
`;
const Right = styled.div`
  grid-area: right;
  display: flex;
  flex-wrap: wrap;
  @media ${desktopAndAbove} {
    flex-direction: column;
    min-height: calc(100vh - var(--current-stack-height));
    width: 100%;
    max-width: 400px;
    margin-left: auto;
    border-left: 1px solid var(--c-nd-200);
  }
`;
const QRCodeContainer = styled(Vertical)`
  grid-area: qr;
  text-align: center;
  margin: auto;
`;
const HowItWorksContainer = styled(Vertical)`
  grid-area: howItWorks;
`;
const QRCodeCanvasContainer = styled.div`
  position: relative;
  isolation: isolate;
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-200);
  border-radius: var(--unit);
`;
const Block = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: min-content;
  border: 1px solid var(--c-nd-200);
  border-left: none;
  border-right: none;
  padding: var(--double-unit) 0;
  &.small {
    min-width: 100%;
    flex: 0;
  }
  @media ${desktopAndAbove} {
    padding: var(--double-unit);
    border-top: none;
    &:last-child {
      border-bottom: none;
    }
    &.small {
      width: auto;
    }
  }
`;
const PowerHour = styled.span`
  --rotate: -8deg;
  &.active {
    display: inline-flex;
    background: linear-gradient(
      92.13deg,
      #ffffff 0%,
      #eabfe2 28.88%,
      #ffffff 48.67%,
      #bedecb 64.58%,
      #ffffff 100%
    );
    border-radius: 4px;
    padding: 0 var(--half-unit);
    transform: rotate(var(--rotate));
    color: var(--c-black);
    font-weight: normal;
    box-shadow: 0 0 16px 8px rgba(var(--c-rgb-white), 0.2);
  }
  &.invite {
    --rotate: 10deg;
    position: absolute;
    right: 0;
    translate: 30% 0%;
    bottom: 100%;
    width: max-content;
  }
`;

const TermsApply = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="text-right">
      <button type="button" onClick={() => setOpen(true)}>
        *
        <FormattedMessage defaultMessage="Referral Terms Apply" />
      </button>
      {!!open && (
        <TermsDialog terms="referralTerms" onClose={() => setOpen(false)} />
      )}
    </div>
  );
};

type Props = {
  currentUser?: OnlineLanding_currentUser | null;
  config?: OnlineLanding_config;
  loading: boolean;
  loadMore: (
    reset: boolean,
    variables: {
      after: string | null;
    },
    forceConnection?: string
  ) => void;
  sport: Sport;
};
const LegacyOnlineLanding = ({
  config,
  currentUser,
  loading,
  loadMore,
  sport,
}: Props) => {
  const { referralProgramConfig } = config || {};
  const { toMonetaryAmount } = useMonetaryAmount();
  const { main: amountValue } = useAmountWithConversion({
    monetaryAmount: toMonetaryAmount(referralProgramConfig?.maxDiscount),
    primaryCurrency: Currency.FIAT,
    hideFractionDigits: true,
  });
  const { isIosApp } = useIsMobileApp();

  const cardsToBuy =
    referralProgramConfig?.numberCardsToBuy ||
    OPTIMISTIC_SLOTS_COUNT_PER_SPORT[sport];

  const loadMoreCards = useCallback(
    ({ after }: { after: string | null }) => {
      loadMore(false, { after }, 'ReferralRewardConnection');
    },
    [loadMore]
  );

  const completedReferrals = currentUser?.referralsCompleted.totalCount || 0;

  const amount = (
    <PowerHour
      className={classNames({
        active: false,
      })}
    >
      <Skeleton loading={!currentUser && loading}>{amountValue}</Skeleton>
    </PowerHour>
  );

  return (
    <Root>
      <PageHeader text={navLabels.inviteFriends} disableBackBrowser />
      <Left>
        <Header>
          <Vertical>
            {!!completedReferrals && completedReferrals >= MAX_REFERRALS && (
              <Warning
                variant="yellow"
                title={messages.goAffiliate}
                message={messages.goAffiliateDescription}
                values={{
                  link: content => <Link to={CREATOR_PROGRAM}>{content}</Link>,
                }}
              />
            )}
            {currentUser?.referralAsReferee?.state ===
              ReferralState.PENDING_REVIEW && (
              <Warning
                variant="yellow"
                title={messages.pendingReview}
                message={messages.pendingReviewDescription}
                values={{
                  link: content => (
                    <Link to={TERMS_AND_CONDITIONS}>{content}</Link>
                  ),
                }}
              />
            )}
            <VerifyPhoneBanner />
            <HeadlineXL as="h1">
              <FormattedMessage
                defaultMessage="Get up to {amount} credit for each referred friend"
                values={{
                  amount,
                }}
              />
            </HeadlineXL>
            <BodyM as="p" color="var(--c-nd-600)">
              <FormattedMessage defaultMessage="Be rewarded for all the successful referrals you bring" />
              *
            </BodyM>
          </Vertical>
        </Header>

        <QRCodeContainer center gap={2}>
          <QRCodeCanvasContainer>
            <QRCode value={currentUser?.referralUrl || ''} />
          </QRCodeCanvasContainer>
          <BodyS as="p">
            <FormattedMessage defaultMessage="Friends can scan your QR code to sign up and win special rewards." />
          </BodyS>
          <InviteFriendsCta
            source={socialShareEventContext.REFERRAL_PROGRAM}
            hideShareButton={isIosApp}
          />
        </QRCodeContainer>
        <HowItWorksContainer gap={2}>
          <BodyL as="h2" bold>
            <FormattedMessage defaultMessage="How it works?" />
          </BodyL>
          <HowItWorks amount={amount} cardsToBuy={cardsToBuy} />
        </HowItWorksContainer>
        <TermsApply />
      </Left>
      <Right>
        {!!currentUser?.referralAsReferee && (
          <Block className="small">
            <ReferralBar currentUser={currentUser} sport={sport} />
          </Block>
        )}
        <Block>
          <Hub
            total={currentUser?.referrals.totalCount || 0}
            completed={completedReferrals}
            unclaimedReferralRewardsCount={
              (completedReferrals > 0 &&
                currentUser?.unclaimedReferralRewardsCount) ||
              0
            }
            sport={sport}
            referralProgramConfigAmount={amountValue}
            cardsRequirements={cardsToBuy}
          />
        </Block>
        <Block>
          <CreditsEarned currentUser={currentUser} />
        </Block>
        {!!currentUser?.cardsReferralRewards &&
          currentUser.cardsReferralRewards.totalCount > 0 && (
            <Block>
              <CardsEarned
                currentUser={currentUser}
                loadMore={loadMoreCards}
                loading={loading}
              />
            </Block>
          )}
      </Right>
    </Root>
  );
};

export const OnlineLanding = (props: Props) => {
  const {
    flags: { enableInviteFriends = false },
  } = useFeatureFlags();

  if (enableInviteFriends) {
    return <SetReferralOnlineLanding />;
  }

  return <LegacyOnlineLanding {...props} />;
};

OnlineLanding.fragments = {
  currentUser: gql`
    fragment OnlineLanding_currentUser on CurrentUser {
      slug
      unclaimedReferralRewardsCount
      referralsCompleted: referrals(
        sport: $sport
        pageSize: 1
        state: COMPLETED
      ) {
        totalCount
      }
      referrals(sport: $sport, pageSize: 10) {
        totalCount
      }
      referralUrl
      ...CreditsEarned_currentUser
      ...CardsEarned_currentUser
      ...ReferralBar_currentUser
    }
    ${CreditsEarned.fragments.currentUser}
    ${CardsEarned.fragments.currentUser}
    ${ReferralBar.fragments.currentUser}
  ` as TypedDocumentNode<OnlineLanding_currentUser>,
  config: gql`
    fragment OnlineLanding_config on Config {
      id
      referralProgramConfig(sport: $sport) {
        id
        numberCardsToBuy
        maxDiscount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<OnlineLanding_config>,
};
